import { ArrowRight, FolderSearch } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import type {
  DashboardSubprojectToWatchByCostDeviationItem,
  DashboardSubprojectToWatchByPendingOrdersItem,
  DashboardSubprojectsToWatch as DashboardSubprojectsToWatchData,
} from '../../types/dashboard';

type DashboardSubprojectsToWatchProps = {
  error: string | null;
  items: DashboardSubprojectsToWatchData;
  loading: boolean;
  onOpen: (route: string) => void;
  formatPercent: (
    value?: string | number | null,
    options?: {
      emptyLabel?: string;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    },
  ) => string;
};

type WatchRow = DashboardSubprojectToWatchByCostDeviationItem | DashboardSubprojectToWatchByPendingOrdersItem;

function SectionEmptyState({ description }: { description: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center">
      <FolderSearch className="h-5 w-5 text-gray-400" />
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
}

function WatchSection({
  description,
  emphasisClassName,
  items,
  onOpen,
  showCostDeviation,
  showPendingOrders,
  title,
  formatPercent,
}: {
  description: string;
  emphasisClassName: string;
  items: WatchRow[];
  onOpen: (route: string) => void;
  showCostDeviation: boolean;
  showPendingOrders: boolean;
  title: string;
  formatPercent: DashboardSubprojectsToWatchProps['formatPercent'];
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <span className="text-sm text-gray-500">{items.length}</span>
      </div>

      {items.length === 0 ? (
        <SectionEmptyState description={`No hay subproyectos para "${title.toLowerCase()}" ahora mismo.`} />
      ) : (
        <div className="w-full">
            <Table className="table-fixed w-full text-sm [&_th]:box-border [&_td]:box-border">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[33%] text-left !whitespace-normal break-words">
                    Subproyecto
                  </TableHead>
                  <TableHead className="w-[41%] text-left !whitespace-normal break-words">
                    Proyecto
                  </TableHead>
                  {showCostDeviation ? (
                    <TableHead className="w-[12%] text-center !whitespace-normal break-words">
                      % desvío
                    </TableHead>
                  ) : null}
                  {showPendingOrders ? (
                    <TableHead className="w-[12%] text-center !whitespace-normal break-words">
                      Pedidos pendientes
                    </TableHead>
                  ) : null}
                  <TableHead className="w-[14%] text-center !whitespace-normal break-words">
                    Acción
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                <TableRow key={`${title}-${item.subproject_id}`}>
                  <TableCell
                    className="align-middle font-medium text-gray-900 whitespace-normal break-words"
                    title={item.subproject_name}
                  >
                    {item.subproject_name}
                  </TableCell>
                  <TableCell
                    className="align-middle whitespace-normal break-words text-gray-700"
                    title={item.project_name}
                  >
                    {item.project_name}
                  </TableCell>
                  {showCostDeviation ? (
                    <TableCell className={`text-center align-middle ${emphasisClassName}`}>
                      {item.cost_deviation_pct == null
                        ? '—'
                        : formatPercent(item.cost_deviation_pct, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                    </TableCell>
                  ) : null}
                  {showPendingOrders ? (
                    <TableCell className={`text-center align-middle ${emphasisClassName}`}>
                      {item.open_orders_count}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-center align-middle">
                    <Button
                      variant="outline"
                      size="sm"
                      className="mx-auto h-8 w-full max-w-[88px] px-2 text-xs"
                      onClick={() => onOpen(item.target_route)}
                    >
                      Abrir
                      <ArrowRight className="ml-1 hidden h-3.5 w-3.5 lg:block" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

export function DashboardSubprojectsToWatch({
  error,
  items,
  loading,
  onOpen,
  formatPercent,
}: DashboardSubprojectsToWatchProps) {
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Subproyectos a vigilar</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : loading ? (
          <div className="space-y-6">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={index} className="space-y-3">
                <Skeleton className="h-5 w-52" />
                <Skeleton className="h-28 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <WatchSection
              title="Desvío de coste"
              description="Sobrecostes que ya superan el umbral configurado."
              emphasisClassName="font-medium text-red-700"
              items={items.cost_deviation}
              onOpen={onOpen}
              showCostDeviation
              showPendingOrders={false}
              formatPercent={formatPercent}
            />
            <div className="border-t border-gray-100 pt-6">
              <WatchSection
                title="Pedidos pendientes"
                description="Subproyectos con demasiados pedidos abiertos en paralelo."
                emphasisClassName="font-medium text-amber-700"
                items={items.pending_orders}
                onOpen={onOpen}
                showCostDeviation={false}
                showPendingOrders
                formatPercent={formatPercent}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
